import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  Contract,
  ContractId,
  ContractInstallment,
  propsContractsState,
  RecentUpload,
} from './interfaces';

const RECENT_UPLOADS_LIMIT = 5;

const initialState: propsContractsState = {
  byId: {},
  allIds: [],
  installmentsByContractId: {},
  snoozedAlerts: {},
  recentUploads: [],
  loading: false,
  saving: false,
  error: false,
  lastFetchedAt: undefined,
};

interface SetContractsBundlePayload {
  contracts: Contract[];
  installmentsByContractId: Record<ContractId, ContractInstallment[]>;
  fetchedAt: string;
}

interface UpsertContractPayload {
  contract: Contract;
  installments: ContractInstallment[];
}

interface UpdateInstallmentPayload {
  contractId: ContractId;
  installment: ContractInstallment;
}

interface SnoozePayload {
  installmentId: string;
  until: string;
}

const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    contractsRequest: (state) => {
      state.loading = true;
      state.error = false;
    },
    contractsListSuccess: (state, action: PayloadAction<SetContractsBundlePayload>) => {
      const byId: Record<ContractId, Contract> = {};
      const allIds: ContractId[] = [];
      action.payload.contracts.forEach((c) => {
        byId[c.id] = c;
        allIds.push(c.id);
      });
      state.byId = byId;
      state.allIds = allIds;
      state.installmentsByContractId = action.payload.installmentsByContractId;
      state.loading = false;
      state.error = false;
      state.lastFetchedAt = action.payload.fetchedAt;
    },
    contractsFailure: (state) => {
      state.loading = false;
      state.saving = false;
      state.error = true;
    },
    contractsSavingStart: (state) => {
      state.saving = true;
      state.error = false;
    },
    contractsSavingDone: (state) => {
      state.saving = false;
    },
    upsertContract: (state, action: PayloadAction<UpsertContractPayload>) => {
      const { contract, installments } = action.payload;
      state.byId[contract.id] = contract;
      if (!state.allIds.includes(contract.id)) {
        state.allIds.unshift(contract.id);
      }
      state.installmentsByContractId[contract.id] = installments;
      state.saving = false;
      state.error = false;
    },
    removeContract: (state, action: PayloadAction<ContractId>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
      delete state.installmentsByContractId[action.payload];
    },
    updateInstallment: (state, action: PayloadAction<UpdateInstallmentPayload>) => {
      const { contractId, installment } = action.payload;
      const list = state.installmentsByContractId[contractId];
      if (!list) return;
      const idx = list.findIndex((i) => i.id === installment.id);
      if (idx < 0) return;
      list[idx] = installment;

      if (installment.status === 'paid') {
        const contract = state.byId[contractId];
        if (contract) {
          const paidCount = list.filter((i) => i.status === 'paid').length;
          const paidSum = list
            .filter((i) => i.status === 'paid')
            .reduce((s, i) => s + i.amount, 0);
          contract.installmentsPaid = paidCount;
          contract.amountPaid = paidSum;
          contract.amountRemaining = Math.max(0, contract.amountTotal - paidSum);
          if (paidCount >= contract.installmentsTotal) {
            contract.status = 'completed';
          }
          contract.updatedAt = new Date().toISOString();
        }
        const nextCandidate = list.find(
          (i) => i.status === 'future' || i.status === 'overdue',
        );
        if (nextCandidate) {
          nextCandidate.status =
            new Date(nextCandidate.dueDate).getTime() < Date.now()
              ? 'overdue'
              : 'next';
        }
      }
    },
    snoozeAlert: (state, action: PayloadAction<SnoozePayload>) => {
      state.snoozedAlerts[action.payload.installmentId] = action.payload.until;
    },
    clearSnooze: (state, action: PayloadAction<string>) => {
      delete state.snoozedAlerts[action.payload];
    },
    pushRecentUpload: (state, action: PayloadAction<RecentUpload>) => {
      const current = state.recentUploads ?? [];
      state.recentUploads = [
        action.payload,
        ...current.filter((u) => u.id !== action.payload.id),
      ].slice(0, RECENT_UPLOADS_LIMIT);
    },
    clearRecentUploads: (state) => {
      state.recentUploads = [];
    },
    clearContracts: () => initialState,
  },
});

export const {
  contractsRequest,
  contractsListSuccess,
  contractsFailure,
  contractsSavingStart,
  contractsSavingDone,
  upsertContract,
  removeContract,
  updateInstallment,
  snoozeAlert,
  clearSnooze,
  pushRecentUpload,
  clearRecentUploads,
  clearContracts,
} = contractsSlice.actions;

export default contractsSlice.reducer;
